import { useState } from 'react';
import {
  TIMBRE_PRESETS,
  type TimbrePreset,
} from '@engine/audio';
import { useSynthStore, type ToneSlot } from '@store/index';
import './SynthPanel.css';

const OSC_TYPES: OscillatorType[] = ['sine', 'triangle', 'square', 'sawtooth'];

/**
 * Synth/timbre panel — mirrors the tuning flow: tone slots (save/switch
 * between 4+ player-customised timbres), preset palette (starting
 * points), then per-parameter visual editors (osc pickers, ADSR
 * preview, wet meter). Everything writes to the currently-active slot
 * so the player builds their own tones.
 */
export function SynthPanel() {
  const slots = useSynthStore((s) => s.slots);
  const activeIdx = useSynthStore((s) => s.activeSlotIndex);
  const setActive = useSynthStore((s) => s.setActiveSlot);
  const addSlot = useSynthStore((s) => s.addSlot);
  const removeSlot = useSynthStore((s) => s.removeSlot);
  const renameSlot = useSynthStore((s) => s.renameSlot);
  const updateSlot = useSynthStore((s) => s.updateSlot);
  const applyPresetToActive = useSynthStore((s) => s.applyPresetToActive);

  const [editingName, setEditingName] = useState<number | null>(null);
  const active = slots[activeIdx]!;

  return (
    <div className="sp">
      {/* Tone slots — one per user preset, swipe to switch, gesture RH palm-back + N fingers. */}
      <section className="sp-sets">
        <div className="sp-sets__row">
          {slots.map((st, i) => {
            const isActive = i === activeIdx;
            const isEditing = editingName === i;
            return (
              <div
                key={i}
                className={'sp-set' + (isActive ? ' sp-set--active' : '')}
                onClick={() => !isEditing && setActive(i)}
                onDoubleClick={() => setEditingName(i)}
              >
                {isEditing ? (
                  <input
                    autoFocus
                    className="sp-set__name-edit"
                    defaultValue={st.name}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v) renameSlot(i, v);
                      setEditingName(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      if (e.key === 'Escape') setEditingName(null);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <div className="sp-set__name">{st.name}</div>
                )}
                <div className="sp-set__sub">
                  {st.osc1}/{st.osc2}
                </div>
                {slots.length > 1 && isActive && (
                  <button
                    type="button"
                    className="sp-set__del"
                    title="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(`Delete "${st.name}"?`)) removeSlot(i);
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
          <button
            type="button"
            className="sp-set sp-set--add"
            onClick={addSlot}
            title="New tone (copy of current)"
          >
            +
          </button>
        </div>
      </section>

      {/* Preset palette — like Shape in Tuning. Tap to load a starting point. */}
      <section>
        <h3 className="sp-h">Palette</h3>
        <div className="sp-tiles">
          {TIMBRE_PRESETS.map((p) => {
            const matches = presetMatches(active, p);
            return (
              <button
                key={p.key}
                type="button"
                className={'sp-tile' + (matches ? ' sp-tile--on' : '')}
                onClick={() => applyPresetToActive(p.key)}
                title={`${p.label} · ${p.tag}`}
              >
                <OscGlyph type={p.osc1} />
                <span className="sp-tile__label">{p.label}</span>
                <span className="sp-tile__tag">{p.tag}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Oscillators — visual waveform picker. */}
      <section>
        <h3 className="sp-h">Oscillator 1</h3>
        <OscPicker
          value={active.osc1}
          onPick={(v) => updateSlot(activeIdx, { osc1: v })}
        />
      </section>
      <section>
        <h3 className="sp-h">Oscillator 2</h3>
        <OscPicker
          value={active.osc2}
          onPick={(v) => updateSlot(activeIdx, { osc2: v })}
        />
        <RangeRow
          label="Detune"
          value={active.detuneCents}
          min={0}
          max={40}
          step={1}
          unit="¢"
          onChange={(v) => updateSlot(activeIdx, { detuneCents: v })}
        />
      </section>

      {/* ADSR envelope — visual curve, editable via sliders. */}
      <section>
        <h3 className="sp-h">Envelope</h3>
        <EnvelopeGraph preset={active} />
        <RangeRow
          label="Attack"
          value={active.attackMs}
          min={1}
          max={600}
          step={1}
          unit="ms"
          onChange={(v) => updateSlot(activeIdx, { attackMs: v })}
        />
        <RangeRow
          label="Decay"
          value={active.decayMs}
          min={20}
          max={2000}
          step={10}
          unit="ms"
          onChange={(v) => updateSlot(activeIdx, { decayMs: v })}
        />
        <RangeRow
          label="Sustain"
          value={Math.round(active.sustainFrac * 100)}
          min={0}
          max={100}
          step={1}
          unit="%"
          onChange={(v) => updateSlot(activeIdx, { sustainFrac: v / 100 })}
        />
        <RangeRow
          label="Tail"
          value={active.tailSec}
          min={0.3}
          max={8}
          step={0.1}
          unit="s"
          onChange={(v) => updateSlot(activeIdx, { tailSec: v })}
        />
      </section>

      {/* Effect send — wet bus amount. */}
      <section>
        <h3 className="sp-h">Space</h3>
        <RangeRow
          label="Wet"
          value={Math.round(active.wet * 100)}
          min={0}
          max={100}
          step={1}
          unit="%"
          onChange={(v) => updateSlot(activeIdx, { wet: v / 100 })}
        />
        <div className="sp-wet__hint">delay 120 ms · feedback 45% · lowpass 3.6 kHz</div>
      </section>

      <p className="sp-gesture">
        Gesture: tay phải xoay lưng + N ngón (giữ 450ms) → chọn Tone N
      </p>
    </div>
  );
}

function presetMatches(slot: ToneSlot, p: TimbrePreset): boolean {
  return (
    slot.osc1 === p.osc1 &&
    slot.osc2 === p.osc2 &&
    slot.detuneCents === p.detuneCents &&
    slot.attackMs === p.attackMs &&
    slot.decayMs === p.decayMs &&
    slot.sustainFrac === p.sustainFrac &&
    slot.tailSec === p.tailSec &&
    slot.wet === p.wet
  );
}

function OscPicker({
  value,
  onPick,
}: {
  value: OscillatorType;
  onPick: (v: OscillatorType) => void;
}) {
  return (
    <div className="sp-osc-picker">
      {OSC_TYPES.map((t) => (
        <button
          key={t}
          type="button"
          className={'sp-osc' + (value === t ? ' sp-osc--on' : '')}
          onClick={() => onPick(t)}
          title={t}
        >
          <OscGlyph type={t} />
          <span className="sp-osc__label">{t}</span>
        </button>
      ))}
    </div>
  );
}

function OscGlyph({ type }: { type: OscillatorType }) {
  return (
    <svg viewBox="0 0 40 20" className="sp-osc__glyph">
      <path d={oscPath(type)} fill="none" stroke="currentColor" strokeWidth={1.6} />
    </svg>
  );
}

function oscPath(type: OscillatorType): string {
  switch (type) {
    case 'sine':
      return 'M 2 10 Q 7 2 12 10 T 22 10 T 32 10 T 38 10';
    case 'triangle':
      return 'M 2 10 L 7 2 L 17 18 L 27 2 L 37 18';
    case 'square':
      return 'M 2 18 L 2 2 L 12 2 L 12 18 L 22 18 L 22 2 L 32 2 L 32 18 L 38 18';
    case 'sawtooth':
      return 'M 2 18 L 12 2 L 12 18 L 22 2 L 22 18 L 32 2 L 32 18 L 38 2';
    default:
      return 'M 2 10 L 38 10';
  }
}

function EnvelopeGraph({ preset }: { preset: TimbrePreset }) {
  const W = 280;
  const H = 72;
  const totalMs = preset.attackMs + preset.decayMs + preset.tailSec * 1000;
  const xAttack = (preset.attackMs / totalMs) * W;
  const xDecayEnd = ((preset.attackMs + preset.decayMs) / totalMs) * W;
  const yPeak = 6;
  const yZero = H - 6;
  const ySustain = yZero - (yZero - yPeak) * preset.sustainFrac;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="sp-env">
      <line x1={0} y1={yZero} x2={W} y2={yZero} stroke="rgba(255,255,255,0.15)" />
      <polygon
        points={`0,${yZero} ${xAttack},${yPeak} ${xDecayEnd},${ySustain} ${W},${yZero}`}
        fill="rgba(255, 210, 74, 0.12)"
      />
      <polyline
        points={`0,${yZero} ${xAttack},${yPeak} ${xDecayEnd},${ySustain} ${W},${yZero}`}
        fill="none"
        stroke="#ffd24a"
        strokeWidth={2}
      />
      <text x={xAttack} y={H - 2} fontSize={9} fill="rgba(255,255,255,0.6)" textAnchor="middle">
        A
      </text>
      <text x={xDecayEnd} y={H - 2} fontSize={9} fill="rgba(255,255,255,0.6)" textAnchor="middle">
        D
      </text>
      <text x={xDecayEnd + 4} y={ySustain - 4} fontSize={9} fill="rgba(255,255,255,0.6)">
        S
      </text>
      <text x={W - 4} y={yZero - 4} fontSize={9} fill="rgba(255,255,255,0.6)" textAnchor="end">
        R
      </text>
    </svg>
  );
}

function RangeRow({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (v: number) => void;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="sp-range">
      <div className="sp-range__head">
        <span className="sp-range__k">{label}</span>
        <span className="sp-range__v">
          {typeof value === 'number' && step < 1 ? value.toFixed(1) : value}
          {unit}
        </span>
      </div>
      <input
        type="range"
        className="sp-range__input"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ background: `linear-gradient(90deg, var(--key-root) ${pct}%, rgba(255,255,255,0.1) ${pct}%)` }}
      />
    </div>
  );
}
